import { RTKQueryTag } from 'src/common/constants';
import type { ApiResponse } from 'src/common/interfaces';
import { apiSlice } from 'src/features/api/api-slice';

import type {
  AddCompanyResponseData,
  AllCompaniesData,
  CompanyData,
  CompanyInstructorData,
  CompanyParams,
  CompanySubscriberData,
  CorporateParams,
  CreateCompanyData,
  CreateInstructorData,
  CreateSeatData,
  DeleteAdminData,
  DeleteSeatData,
  EnableSSOData,
  GetPreSignedUrlBody,
  GetPreSignedUrlResponse,
  ToggleData,
  UpdateCompanyDetailsProps,
  UpdateDeadlineData,
  UpdateRenewDashboardData,
  UpdateTokenData,
} from './corporate.types';

export const extendedApiSlice = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getAllCompanies: builder.query<AllCompaniesData | undefined, void>({
      query: () => `admin/companies`,
      providesTags: [RTKQueryTag.AllCompanies],
      transformResponse: (response: ApiResponse<AllCompaniesData>) => response.data,
    }),
    getSingleCompany: builder.query<ApiResponse<CompanyData>, CompanyParams>({
      query: ({ companyId }) => ({
        url: 'admin/companies/details',
        params: { companyId },
      }),
      providesTags: (result, error, { companyId }) => [{ type: RTKQueryTag.SingleCompany, id: companyId }],
    }),
    getAllInstructorsOfACompany: builder.query<ApiResponse<CompanyInstructorData>, CorporateParams>({
      query: ({ companyId, page, limit, search = '', sort }) => ({
        url: 'admin/companies/users',
        params: { companyId, page, limit, search, sort },
      }),
      providesTags: (result, error, { companyId }) => [{ type: RTKQueryTag.CompanyInstructors, id: companyId }],
    }),
    getAllSubscribersOfACompany: builder.query<ApiResponse<CompanySubscriberData>, CorporateParams>({
      query: ({ companyId, page, limit, search = '', sort }) => ({
        url: 'admin/companies/subscribers',
        params: { companyId, page, limit, search, sort },
      }),
      providesTags: (result, error, { companyId }) => [{ type: RTKQueryTag.CompanySubscriber, id: companyId }],
    }),
    addCompany: builder.mutation<ApiResponse<AddCompanyResponseData>, CreateCompanyData>({
      query: ({ name, url, deadline, isTrial, adminEmails, type }) => ({
        url: `admin/companies`,
        method: 'POST',
        body: { name, url, deadline, isTrial, adminEmails, type },
      }),
      invalidatesTags: [RTKQueryTag.AllCompanies],
    }),
    addSeat: builder.mutation<ApiResponse, CreateSeatData>({
      query: ({ companyId, userEmails, blankUsers, subDuration, seatType }) => ({
        url: `admin/companies/seats`,
        method: 'POST',
        body: { companyId, userEmails, blankUsers, subDuration, seatType },
      }),
      invalidatesTags: (result, error, { companyId }) => [
        RTKQueryTag.CompanySubscriber,
        { type: RTKQueryTag.SingleCompany, id: companyId },
      ],
    }),
    addInstructor: builder.mutation<ApiResponse, CreateInstructorData>({
      query: ({ companyId, adminEmails }) => ({
        url: `admin/companies/users`,
        method: 'POST',
        body: { companyId, adminEmails },
      }),
      invalidatesTags: [RTKQueryTag.CompanyInstructors],
    }),
    deleteSeat: builder.mutation<ApiResponse, DeleteSeatData>({
      query: ({ companyId, seatId }) => ({
        url: `admin/companies/seats`,
        method: 'DELETE',
        body: { companyId, seatId },
      }),
      invalidatesTags: (result, error, { companyId }) => [
        RTKQueryTag.CompanySubscriber,
        { type: RTKQueryTag.SingleCompany, id: companyId },
      ],
    }),
    updateApiKeysStatus: builder.mutation<ApiResponse, ToggleData>({
      query: ({ companyId, status }) => ({
        url: `admin/companies/toggle-api-key`,
        method: 'PATCH',
        body: { companyId, status },
      }),
      invalidatesTags: (result, error, { companyId }) => [{ type: RTKQueryTag.SingleCompany, id: companyId }],
    }),
    updateCustomPathsStatus: builder.mutation<ApiResponse, ToggleData>({
      query: ({ companyId, status }) => ({
        url: `admin/companies/toggle-custom-paths`,
        method: 'PATCH',
        body: { companyId, status },
      }),
      invalidatesTags: (result, error, { companyId }) => [{ type: RTKQueryTag.SingleCompany, id: companyId }],
    }),
    toggleSocSimAccess: builder.mutation<ApiResponse, Pick<ToggleData, 'companyId'>>({
      query: ({ companyId }) => ({
        url: `admin/companies/toggle-soc-sim`,
        method: 'PATCH',
        body: { companyId },
      }),
      invalidatesTags: (result, error, { companyId }) => [{ type: RTKQueryTag.SingleCompany, id: companyId }],
    }),
    updateTokens: builder.mutation<ApiResponse, UpdateTokenData>({
      query: ({ companyId, tokens, operation }) => ({
        url: `admin/companies/tokens`,
        method: 'PATCH',
        body: { companyId, tokens, operation },
      }),
      invalidatesTags: (result, error, { companyId }) => [{ type: RTKQueryTag.SingleCompany, id: companyId }],
    }),
    updateDeadline: builder.mutation<ApiResponse, UpdateDeadlineData>({
      query: ({ companyId, deadline, isTrial }) => ({
        url: `admin/companies/deadline`,
        method: 'PATCH',
        body: { companyId, deadline, isTrial },
      }),
      invalidatesTags: (result, error, { companyId }) => [{ type: RTKQueryTag.SingleCompany, id: companyId }],
    }),
    renewDashboard: builder.mutation<ApiResponse, UpdateRenewDashboardData>({
      query: ({ companyId, renewalDate }) => ({
        url: `admin/companies/renew-dashboard`,
        method: 'PATCH',
        body: { companyId, renewalDate },
      }),
      invalidatesTags: (result, error, { companyId }) => [{ type: RTKQueryTag.SingleCompany, id: companyId }],
    }),
    enableSSO: builder.mutation<ApiResponse, EnableSSOData>({
      query: ({ companyId }) => ({
        url: `admin/companies/enable-sso`,
        method: 'PATCH',
        body: { companyId },
      }),
      invalidatesTags: (result, error, { companyId }) => [{ type: RTKQueryTag.SingleCompany, id: companyId }],
    }),
    updateCompanyDetails: builder.mutation<ApiResponse, UpdateCompanyDetailsProps>({
      query: (body) => ({
        url: 'admin/companies/details',
        method: 'PATCH',
        body,
      }),
      invalidatesTags: (result, error, { companyId }) => [
        RTKQueryTag.AllCompanies,
        { type: RTKQueryTag.SingleCompany, id: companyId },
      ],
    }),
    getCompanyLogoPreSignedUrl: builder.query<GetPreSignedUrlResponse, GetPreSignedUrlBody>({
      query: (params) => ({
        url: 'admin/companies/logo-upload-url',
        method: 'GET',
        params,
      }),
      transformResponse: (response: ApiResponse<GetPreSignedUrlResponse>) => response.data as GetPreSignedUrlResponse,
    }),
    deleteAdmin: builder.mutation<ApiResponse, DeleteAdminData>({
      query: ({ companyId, identifier }) => ({
        url: `admin/companies/admin-users`,
        method: 'DELETE',
        body: { companyId, identifier },
      }),
      invalidatesTags: (result, error, { companyId }) => [
        RTKQueryTag.CompanyInstructors,
        { type: RTKQueryTag.SingleCompany, id: companyId },
      ],
    }),
  }),
});

export const {
  useGetSingleCompanyQuery,
  useGetAllCompaniesQuery,
  useGetAllInstructorsOfACompanyQuery,
  useGetAllSubscribersOfACompanyQuery,
  useAddCompanyMutation,
  useAddSeatMutation,
  useAddInstructorMutation,
  useDeleteSeatMutation,
  useUpdateApiKeysStatusMutation,
  useUpdateCustomPathsStatusMutation,
  useUpdateTokensMutation,
  useUpdateDeadlineMutation,
  useRenewDashboardMutation,
  useEnableSSOMutation,
  useToggleSocSimAccessMutation,
  useUpdateCompanyDetailsMutation,
  useLazyGetCompanyLogoPreSignedUrlQuery,
  useDeleteAdminMutation,
} = extendedApiSlice;
